import { useAction, useAtom } from '@reatom/npm-react'
import { useCallback } from 'react'
import { Virtuoso } from 'react-virtuoso'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { AttentionIcon, CircularLoader } from '@blade/ui-kit'
import { useBreakpoint } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage, PausedBadge, SearchLoupeIcon } from '@root/shared/ui-kit'

import { walletAlertsListStateAtom } from '../../atoms/reatom-tracking-wallets-alerts-list'
import { trackingWalletAlertsTableModel } from '../../atoms/reatom-tracking-wallets-alerts-table'

import { TrackingWalletFollowingUpdateCard } from './tracking-wallet-following-update-card'

export const TrackingWalletFollowingUpdatesList = () => {
  const { t } = useTranslation()
  const [paused, setPaused] = useAtom(trackingWalletAlertsTableModel.paused)

  const [state] = useAtom(walletAlertsListStateAtom)
  const loadMore = useAction(trackingWalletAlertsTableModel.load)
  const isMobile = useBreakpoint() === 'mobile'

  const handleMouseOver = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isMobile) {
        return
      }
      e.stopPropagation()
      setPaused(true)
    },
    [setPaused, isMobile],
  )

  return AsyncStateN.matchWaiting(state, {
    Waiting: () => (
      <Root>
        <InfoMessage
          intent="empty"
          variant="dt"
          title={t('common.loading')}
          description={undefined}
          icon={<CircularLoader size={18} />}
          withBackground
        />
      </Root>
    ),
    Error: () => (
      <Root>
        <InfoMessage
          intent="error"
          variant="dt"
          icon={<AttentionIcon />}
          title={t('common.loading-error')}
          description={t('common.something-went-wrong')}
          onRetry={loadMore}
          withBackground
        />
      </Root>
    ),
    Success: (data) =>
      data.length === 0 ? (
        <Root>
          <InfoMessage
            intent="empty"
            variant="dt"
            title={t('common.empty-unfortunately')}
            description={t('common.no-items-found')}
            icon={<SearchLoupeIcon />}
            withBackground
          />
        </Root>
      ) : (
        <Root
          onMouseOver={handleMouseOver}
          onMouseLeave={() => {
            setPaused(false)
          }}
        >
          {paused ? <PausedBadgeStyled paused={paused} /> : undefined}
          <Virtuoso
            data={data}
            itemContent={(index, trade) => (
              <Item data-even={!(index % 2)}>
                <TrackingWalletFollowingUpdateCard
                  key={trade.transactionHash}
                  trade={trade}
                />
              </Item>
            )}
          />
        </Root>
      ),
  })
}

const Root = styled.div`
  display: grid;
  min-height: 0;
  overflow: auto;
  position: relative;
`
const Item = styled.div`
  background: var(--background-2);
  &[data-even='true'] {
    background: var(--background-1);
  }
  border-bottom: 1px solid var(--background-3);
`

const PausedBadgeStyled = styled(PausedBadge)`
  position: absolute;
  z-index: 1;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--amber-3);
  box-shadow: 0 0 8px 2px var(--appearanceA-2);
`
